import React, { useEffect, useState } from "react";
import { useSearch } from "../Contexts/SearchContext";
import Loading from "../Loading";
import { getSuggestionHandler, postSearchHandler_search } from "../Api";
import SearchResults from "./SearchResults/SearchResults";
import {
  setFilterState,
  getActiveFilters,
} from "../../helpers";
import FilterDrawer from "./FilterDrawer/FilterDrawer";
import "./SearchContainer.scss";
import Popover from "../Popover";
import SearchBar from "./SearchBar/SearchBar";
import {
  SEARCH_RESULTS_TO_SHOW_DESKTOP,
  SEARCH_RESULTS_TO_SHOW_MOBILE,
  SEARCH_RESULTS_TO_SHOW_TABLET
} from "../../constants/site-search";

const SearchContainer = () => {
  const { state, dispatch } = useSearch();

  // context
  const {
    filteredResults,
    emptyResults,
    selectedFilters,
    isReady,
    filterDrawerIsOpen,
    initialRenderRequired,
    searchError,
    isLoading,
    showResults,
    placeholderText,
    componentElement,
    searchTerm,
    resultsToShow_mobile,
    resultsToShow_tablet,
    resultsToShow_desktop,
  } = state;

  const [resultCountMobile, setResultCountMobile] = useState(0);
  const [resultCountTablet, setResultCountTablet] = useState(0);
  const [resultCountDesktop, setResultCountDesktop] = useState(0);

  useEffect(() => {
    if (!isReady) {
      dispatch({ type: "UPDATE_IS_LOADING", payload: true });
      dispatch({ type: "UPDATE_READINESS", payload: true });
    }
  }, [isReady]);

  /**
   * Runs a filter search API request if one is pending and
   * if filters are present. Sets the pending flag to false.
   */
  useEffect(() => {
    if (initialRenderRequired) {
      dispatch({ type: "SET_INITIAL_RENDER_REQUISITE", payload: false });
    }
  }, [initialRenderRequired]);

  useEffect(() => {
    if (filteredResults) {
      setResultCountMobile(getDisplayCount(resultsToShow_mobile));
      setResultCountTablet(getDisplayCount(resultsToShow_tablet));
      setResultCountDesktop(getDisplayCount(resultsToShow_desktop));
    }
  }, [filteredResults]);

  useEffect(() => {
    setResultCountMobile(getDisplayCount(resultsToShow_mobile));
  }, [resultsToShow_mobile]);

  useEffect(() => {
    setResultCountTablet(getDisplayCount(resultsToShow_tablet));
  }, [resultsToShow_tablet]);

  useEffect(() => {
    setResultCountDesktop(getDisplayCount(resultsToShow_desktop));
  }, [resultsToShow_desktop]);

  /**
   * Updates the url query params each time filters are updated.
   */
  useEffect(() => {
    if (initialRenderRequired) return;
    updateFilterQueryParams();
    updateResultsToShow();
  }, [selectedFilters]);

  const getDisplayCount = (resultsToShowCount) => {
    return Math.min(resultsToShowCount, filteredResults.length);
  }

  const updateFilterQueryParams = () => {
    const url = new URL(window.location.href);
    let filterParams = [];
    selectedFilters.map((filter) => {
      let filterId = encodeURI(filter.tagId);
      if (!filterParams.includes(filterId)) {
        filterParams.push(filterId);
      }
    });
    if (filterParams.length > 0) url.searchParams.set("filters", filterParams.join("&"));
    else url.searchParams.delete("filters");
    window.history.replaceState({}, "", url.href);
  }

  const updateResultsToShow = () => {
    dispatch({ type: "UPDATE_RESULTS_TO_SHOW_MOBILE", payload: SEARCH_RESULTS_TO_SHOW_MOBILE });
    dispatch({ type: "UPDATE_RESULTS_TO_SHOW_TABLET", payload: SEARCH_RESULTS_TO_SHOW_TABLET });
    dispatch({ type: "UPDATE_RESULTS_TO_SHOW_DESKTOP", payload: SEARCH_RESULTS_TO_SHOW_DESKTOP });
  }

  const getSuggestions = (_searchTerm) => {
    return getSuggestionHandler(_searchTerm, dispatch);
  };

  const doSearch = (_searchTerm, dispatch) => {
    // _searchTerm comes from when a user clicks an item in the popular search terms or suggestions dropdowns
    // it should take priority
    let term = _searchTerm || searchTerm;
    return postSearchHandler_search(term, dispatch);
  };

  const dispatchActiveFilters = () => {
    dispatch({
      type: "UPDATE_SELECTED_FILTERS",
      payload: getActiveFilters(componentElement),
    });
  }

  const removeFilter = (filterId) => {
    setFilterState(filterId, componentElement, false);
    dispatchActiveFilters();
  };

  const removeAllFilters = () => {
    selectedFilters.map((filterObject) => { setFilterState(filterObject.tagId, componentElement, false); });
    dispatch({ type: "UPDATE_SELECTED_FILTERS", payload: [] });
  }

  const renderSortByIcon = `<div class='cmp-site-search-icon'><svg xmlns="http://www.w3.org/2000/svg" width="20.57" height="18" viewBox="0 0 20.57 18"><path id="Icon_awesome-sort-amount-down" data-name="Icon awesome-sort-amount-down" d="M12.217,17.678H9.645A.643.643,0,0,0,9,18.322v1.286a.643.643,0,0,0,.643.643h2.572a.643.643,0,0,0,.643-.643V18.322a.643.643,0,0,0-.643-.643ZM7.074,15.107H5.145V2.893A.643.643,0,0,0,4.5,2.25H3.217a.643.643,0,0,0-.643.643V15.107H.645a.643.643,0,0,0-.454,1.1l3.214,3.857a.643.643,0,0,0,.909,0L7.529,16.2a.644.644,0,0,0-.455-1.1ZM17.36,7.393H9.645A.643.643,0,0,0,9,8.036V9.321a.643.643,0,0,0,.643.643H17.36A.643.643,0,0,0,18,9.322V8.036A.643.643,0,0,0,17.36,7.393Zm-2.572,5.143H9.645A.643.643,0,0,0,9,13.178v1.286a.643.643,0,0,0,.643.643h5.143a.643.643,0,0,0,.643-.643V13.178a.643.643,0,0,0-.643-.643ZM19.931,2.25H9.645A.643.643,0,0,0,9,2.893V4.178a.643.643,0,0,0,.643.643H19.931a.643.643,0,0,0,.643-.643V2.893A.643.643,0,0,0,19.931,2.25Z" transform="translate(-0.004 -2.25)" fill="#3f4145"/></svg><div class="icon">&rsaquo;</div></div>`;

  // renders
  return (
    <section className="cmp-site-search">
      <div className="cmp-site-search__left-rail">
        <FilterDrawer
          postSearchHandler={doSearch}
          getSuggestionHandler={getSuggestions}
          placeholderText={placeholderText}
        />
      </div>
      <div
        className={`cmp-site-search__main-section${
          filterDrawerIsOpen ? " hide" : ""
        }`}
      >
        <div className="cmp-site-search__search-container results-search-bar">
          <SearchBar/>
        </div>
        {showResults && (
          <>
            <div className="site-search-results-heading">
              Results{searchTerm && searchTerm.length && ` for '${searchTerm}'`}
            </div>
          </>
        )}

        {showResults && (
          <div className="cmp-site-search__filtered-results-count">
            <div className="count-message">Showing <span className="count-mobile">{resultCountMobile}</span>
              <span className="count-tablet">{resultCountTablet}</span>
              <span className="count-desktop">{resultCountDesktop}</span> out of {filteredResults.length}</div>
            <div className="cmp-site-search__filtered-results-options">
              <Popover
                trigger={renderSortByIcon}
                dispatch={dispatch}
                componentElement={componentElement}
                siteType='Site Search'
                portal={[
                  {
                    id: 1,
                    content: "Relevance",
                    type: "relevance",
                  },
                  {
                    id: 2,
                    content: "Most Popular",
                    type: "popular",
                  },
                  {
                    id: 3,
                    content: "A to Z",
                    type: "asc",
                  },
                  {
                    id: 4,
                    content: "Z to A",
                    type: "desc",
                  },
                ]}
              />
            </div>
          </div>
        )}
        {selectedFilters && selectedFilters.length > 0 && (
          <div className="cmp-site-search__selected-filter-buttons">
            {selectedFilters.map((filterObject) => {
                if (filterObject.type === "filter") {
                  return (
                    <>
                      <div
                        className="selected-filter-button"
                        data-filter-id={filterObject.tagId}
                      >
                        <div className="selected-filter-button__text">
                          {filterObject.label}
                        </div>
                        <div className="close" onClick={() => removeFilter(filterObject.tagId)}/>
                      </div>
                    </>
                  );
                }
              })}
              <button type="reset" className="cmp-site-search__remove-all" onClick={() => removeAllFilters()}>
                Remove all
              </button>
          </div>
        )}
        {filteredResults && showResults && (
          <>
            <SearchResults emptyResults={emptyResults} />
          </>
        )}
        {searchError && (
          <p className="error">{searchError ? searchError : emptyResults}</p>
        )}
      </div>
      {isLoading && (<Loading size="large" overlay={true}/>)}
    </section>
  );
};

export default SearchContainer;
